// Shared, cached loader for the battle sprites so they can be PRELOADED while the
// player is still on the loading screen — by the time the lobby + live battle
// mount, every asset (sprites, all map backgrounds, all lobby cover scenes) is
// already decoded and warm in the browser cache, so nothing pops in late.
//
// Sprites come from ONE packed atlas (atlas.<hash>.webp + .json, built by
// scripts/build-battle-atlas.mjs): a single fetch/decode, zero runtime bbox scans
// (bbox is precomputed in the index), and the atlas stays ONE HTMLImageElement —
// never sliced into a canvas (iOS WebKit purges retained canvases). A missing key
// falls back to the old per-file load; a missing atlas falls back entirely.

import {
  loadKeyedSprite, type Sprite,
  allBodySources, splitBodySources, WEAPON_SRC, weaponSrcFor, allWeaponSkinSources, WEAPON_ICON_SRC, GRENADE_SRC, SMOKE_NADE_SRC, FLASH_NADE_SRC, KNIFE_SRC,
  GRENADE_ICON_SRC, SMOKE_ICON_SRC, MEDKIT_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, KNIFE_KILL_ICON_SRC, HEADSHOT_ICON_SRC,
} from '../spriteLayout';
import { loadFrames, loadPlain, sliceRunSheet } from '../render/loadAssets';
import { ATLAS_INDEX, ATLAS_PAGES } from './atlasManifest';
import { bustCache } from '../render/battleAssetVersion';
import { getAsset, getAssetImage, pruneAssets } from '../../../lib/assetStore';
import { soundUrl, UI_SOUND_NAMES } from '../../../lib/audioEngine';
import { SFX } from './sfx';

const RUN_SUFFIX = '_legs_run';
import type { Frame } from '../render/types';
import { type RushMidMap, type Team, RUSH_MID_MAPS } from '@cs-shot/battle-sim';

const EXPLOSION = '/battle/explosion.webp';
const TEAM_ICON: Record<Team, string> = { t: '/battle/t.webp', ct: '/battle/ct.webp' };
const DEFAULT_MAP: RushMidMap = 'dust2';
const bgSrc = (map: RushMidMap) => `/battle/bg_${map}.webp`;
const coverSrc = (map: RushMidMap) => `/battle/cover_${map}.webp`;
const LOADING_ART = '/battle/rush_mid_loading.webp';

/** Every asset URL the store should retain — the atlas index + pages, per-map bgs/covers, team
 *  emblems, explosion strip, the loading art, and every sound (battle sfx + music + UI). The
 *  prune drops anything else, so a new build's fresh content-hashed / ?v= URLs evict the old. */
function battleAssetKeepSet(): string[] {
  return [
    ATLAS_INDEX,
    ...ATLAS_PAGES,
    bustCache(TEAM_ICON.t), bustCache(TEAM_ICON.ct),
    bustCache(EXPLOSION),
    bustCache(LOADING_ART),
    ...RUSH_MID_MAPS.map((m) => bustCache(bgSrc(m))),
    ...RUSH_MID_MAPS.map((m) => bustCache(coverSrc(m))),
    ...[...SFX, 'menu_theme', ...UI_SOUND_NAMES].map(soundUrl),
  ];
}

export interface BattleAssets {
  bg: HTMLImageElement;
  explosionImg: ImageBitmap | HTMLImageElement;
  explosionFrames: Frame[];
  teamIcons: Record<Team, HTMLImageElement>;
  bodies: Record<string, Sprite>;
  /** per-skin sliced run-cycle frames, keyed by skin prefix. */
  runFrames: Record<string, Sprite[]>;
  weapons: Record<string, Sprite>;
  /** weapon-skin variant sprites, keyed by skin name (e.g. `ak_gold`) — per-fighter weapon skins */
  weaponSkins: Record<string, Sprite>;
  nade: Sprite;
  smokeNade: Sprite;
  flashNade: Sprite;
  knife: Sprite;
  utilIcons: Record<'grenade' | 'smoke' | 'medkit' | 'flash' | 'knife', Sprite>;
  /** white weapon icons (ak/m4/awp/lmg) for the kill feed — same set the match log uses */
  weaponIcons: Record<string, Sprite>;
  /** CS killfeed headshot mark, drawn after the victim on a headshot kill */
  headshotIcon: Sprite;
  /** knife-slash swoosh + smoke-cloud effect sprites, drawn on the effect layer */
  slash: Sprite;
  smokeCloud: Sprite;
  /** spent brass shell, ejected on every shot */
  casing: Sprite;
}

/** map-independent shared assets — loaded once, reused for every map. Holds EVERY
 * map background + lobby cover scene so the loading screen warms all of them. */
type SharedAssets = Omit<BattleAssets, 'bg'> & {
  bgs: Record<RushMidMap, HTMLImageElement>;
  /** map cover scenes — preloaded (retained so the browser cache stays warm for
   * the fighter tab's cover <img>), not consumed by the canvas renderer. */
  covers: Record<RushMidMap, HTMLImageElement>;
};

const BLOOD = ['blood1', 'blood2', 'blood3', 'blood4', 'blood5', 'blood6'];

// per-file assets NOT in the sprite atlas: every map bg + lobby cover, the explosion strip
// (multi-frame band-scan), and the T/CT emblems (drawn as plain <img>). These stay per-file.
const NON_ATLAS_FILES = RUSH_MID_MAPS.length * 2 + 1 + 2;
// the atlas download is byte-metered onto this many progress units (a smooth MB-honest bar);
// the per-file remainder ticks one unit each.
const ATLAS_PROGRESS_UNITS = 100;

/** Number of discrete load units for the combined progress total: the byte-metered atlas
 *  plus the per-file remainder (maps, explosion, team emblems). */
export function battleAssetCount(): number {
  return ATLAS_PROGRESS_UNITS + NON_ATLAS_FILES;
}

// --- atlas index / sprite construction ---
interface AtlasBox { page: number; x: number; y: number; w: number; h: number; bbox: [number, number, number, number]; }
interface AtlasFrames { frames: AtlasBox[]; }
interface AtlasIndex {
  hash: string;
  pages: { file: string; w: number; h: number; bytes: number }[];
  sprites: Record<string, AtlasBox | AtlasFrames>;
}

const atlasKey = (src: string): string => src.split('?')[0].split('/').pop() ?? src;
const isFrames = (e: AtlasBox | AtlasFrames): e is AtlasFrames => 'frames' in e;
const spriteFromBox = (pages: HTMLImageElement[], e: AtlasBox): Sprite => ({
  canvas: pages[e.page], sx: e.x, sy: e.y, sw: e.w, sh: e.h,
  bx: e.bbox[0], by: e.bbox[1], bw: e.bbox[2], bh: e.bbox[3],
});

async function loadAtlas(onByte: (frac: number) => void): Promise<{ idx: AtlasIndex; pages: HTMLImageElement[] } | null> {
  try {
    const idx: AtlasIndex = await getAsset(ATLAS_INDEX).then((b) => b.text()).then((t) => JSON.parse(t));
    const totalBytes = idx.pages.reduce((a, p) => a + p.bytes, 0) || 1;
    // small per-group pages — fetch every page in parallel with byte-metered progress; each
    // decodes in ms as its own HTMLImageElement (no giant single-page decode to freeze on).
    // getAssetImage serves each page from the IDB store (iOS TMA stops re-downloading them).
    let loaded = 0;
    const onChunk = (n: number) => { loaded += n; onByte(loaded / totalBytes); };
    const pages = await Promise.all(idx.pages.map((p) => getAssetImage(p.file, onChunk)));
    return { idx, pages };
  } catch {
    return null; // atlas absent/failed → whole battle load falls back to per-file below
  }
}

let sharedCache: Promise<SharedAssets> | null = null;
let ready = false;

/** True once the shared battle assets have finished loading (sync — to skip the loading screen). */
export function isBattleAssetsReady(): boolean {
  return ready;
}

/** Run tasks with a bounded concurrency so the per-file FALLBACK path doesn't decode ~130
 *  full-res sprites at once — that peak OOM-kills low-RAM Android WebViews. A small pool keeps
 *  the same work, lower memory peak. (The atlas path builds sprites synchronously, no pool.) */
async function runPool(tasks: Array<() => Promise<void>>, limit: number): Promise<void> {
  let next = 0;
  const worker = async () => { while (next < tasks.length) await tasks[next++](); };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
}

const SPRITE_LOAD_CONCURRENCY = 6;

function loadShared(onUnit?: () => void): Promise<SharedAssets> {
  if (sharedCache) return sharedCache;
  sharedCache = (async () => {
    void pruneAssets(battleAssetKeepSet()); // evict prior-build entries before warming this one
    const tick = () => { try { onUnit?.(); } catch { /* ignore */ } };
    const bodies: Record<string, Sprite> = {};
    const runFrames: Record<string, Sprite[]> = {};
    const weapons: Record<string, Sprite> = {};
    const weaponSkins: Record<string, Sprite> = {};
    const weaponIcons: Record<string, Sprite> = {};
    const utilIcons: Record<string, Sprite> = {};
    const bgs: Record<string, HTMLImageElement> = {};
    const covers: Record<string, HTMLImageElement> = {};

    // 1) the sprite atlas — byte-metered onto ATLAS_PROGRESS_UNITS progress ticks
    let atlasTicks = 0;
    const onByte = (frac: number) => {
      const want = Math.min(ATLAS_PROGRESS_UNITS, Math.floor(frac * ATLAS_PROGRESS_UNITS));
      while (atlasTicks < want) { atlasTicks++; tick(); }
    };
    const atlas = await loadAtlas(onByte);
    while (atlasTicks < ATLAS_PROGRESS_UNITS) { atlasTicks++; tick(); } // top up even if the atlas failed

    const atlasGet = (src: string): Sprite | null => {
      if (!atlas) return null;
      const e = atlas.idx.sprites[atlasKey(src)];
      return e && !isFrames(e) ? spriteFromBox(atlas.pages, e) : null;
    };
    const atlasFrames = (src: string): Sprite[] | null => {
      if (!atlas) return null;
      const e = atlas.idx.sprites[atlasKey(src)];
      return e && isFrames(e) ? e.frames.map((fr) => spriteFromBox(atlas.pages, fr)) : null;
    };

    // 2) build every keyed sprite from the atlas; a missing key loads per-file (fallback pool)
    const fallbacks: Array<() => Promise<void>> = [];
    const putSprite = (target: Record<string, Sprite>, key: string, src: string) => {
      const sp = atlasGet(src);
      if (sp) { target[key] = sp; return; }
      fallbacks.push(() => loadKeyedSprite(src).then((s) => { target[key] = s; }).catch(() => { /* optional */ }));
    };

    for (const k of BLOOD) putSprite(bodies, k, `/battle/${k}.webp`);
    for (const { key, src } of allBodySources()) putSprite(bodies, key, src);
    for (const { key, src } of splitBodySources()) {
      if (key.endsWith(RUN_SUFFIX)) {
        const prefix = key.slice(0, -RUN_SUFFIX.length);
        const fr = atlasFrames(src);
        if (fr) runFrames[prefix] = fr;
        else fallbacks.push(() => loadKeyedSprite(src).then((s) => sliceRunSheet(s, 4)).then((f) => { runFrames[prefix] = f; }).catch(() => { /* optional */ }));
      } else {
        putSprite(bodies, key, src);
      }
    }
    for (const k of Object.keys(WEAPON_SRC)) putSprite(weapons, k, weaponSrcFor(k, 0));
    for (const { name, src } of allWeaponSkinSources()) putSprite(weaponSkins, name, src);
    for (const [k, src] of Object.entries(WEAPON_ICON_SRC)) putSprite(weaponIcons, k, src);
    putSprite(weaponIcons, 'knife', KNIFE_KILL_ICON_SRC);
    putSprite(utilIcons, 'grenade', GRENADE_ICON_SRC);
    putSprite(utilIcons, 'smoke', SMOKE_ICON_SRC);
    putSprite(utilIcons, 'medkit', MEDKIT_ICON_SRC);
    putSprite(utilIcons, 'flash', FLASH_ICON_SRC);
    putSprite(utilIcons, 'knife', KNIFE_ICON_SRC);

    // singletons that live in their own fields — atlas or fallback, resolved below
    const single = (src: string): Promise<Sprite> => {
      const sp = atlasGet(src);
      return sp ? Promise.resolve(sp) : loadKeyedSprite(src);
    };
    const [nade, smokeNade, flashNade, knife, headshotIcon, slash, smokeCloud, casing] = await Promise.all([
      single(GRENADE_SRC), single(SMOKE_NADE_SRC), single(FLASH_NADE_SRC), single(KNIFE_SRC),
      single(HEADSHOT_ICON_SRC), single('/battle/slash.webp'), single('/battle/smoke.webp'), single('/battle/casing.webp'),
    ]);

    // 3) per-file assets NOT in the atlas (maps, explosion, team emblems) + any sprite fallbacks
    let explosion!: Awaited<ReturnType<typeof loadFrames>>;
    let tIcon!: HTMLImageElement; let ctIcon!: HTMLImageElement;
    await runPool([
      ...fallbacks,
      () => loadFrames(EXPLOSION).then((r) => { explosion = r; tick(); }),
      () => loadPlain(TEAM_ICON.t).then((r) => { tIcon = r; tick(); }),
      () => loadPlain(TEAM_ICON.ct).then((r) => { ctIcon = r; tick(); }),
      ...RUSH_MID_MAPS.map((m) => () => loadPlain(bgSrc(m)).then((img) => { bgs[m] = img; tick(); })),
      ...RUSH_MID_MAPS.map((m) => () => loadPlain(coverSrc(m)).then((img) => { covers[m] = img; tick(); })),
    ], SPRITE_LOAD_CONCURRENCY);

    weaponIcons.grenade = utilIcons.grenade; // reuse the same white grenade icon for grenade kills
    ready = true;
    return {
      explosionImg: explosion.img, explosionFrames: explosion.frames,
      teamIcons: { t: tIcon, ct: ctIcon }, bodies, runFrames, weapons, weaponSkins, weaponIcons, headshotIcon, nade, smokeNade, flashNade, knife, slash, smokeCloud, casing, utilIcons, bgs, covers,
    };
  })();
  return sharedCache;
}

/** Load the battle assets for a given map. Everything is preloaded once and cached;
 * the per-map background is just picked from the shared set. `onUnit` fires once per
 * loaded asset (first uncached run). Safe to call repeatedly. */
export function loadBattleAssets(map: RushMidMap = DEFAULT_MAP, onUnit?: () => void): Promise<BattleAssets> {
  return loadShared(onUnit).then((shared) => {
    const { bgs, covers: _covers, ...rest } = shared;
    return { bg: bgs[map], ...rest };
  });
}
